import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Calculator, Lock, Mail } from 'lucide-react';
import ValidatedInput from '../components/ValidatedInput';
import TurnstileWidget, { isTurnstileEnabled } from '../components/TurnstileWidget';
import { validationRules } from '../utils/validation';
import '../components/ValidatedInput.css';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/dashboard';
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm();

  // Generate device fingerprint
  const getDeviceFingerprint = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
    const fingerprint = canvas.toDataURL();
    return btoa(fingerprint).substring(0, 20) + navigator.userAgent.substring(0, 20);
  };

  const onSubmit = async (data) => {
    if (isTurnstileEnabled() && !turnstileToken) {
      setError('root', { message: 'Please complete the verification check' });
      return;
    }

    setIsLoading(true);
    try {
      // Add bot protection fields
      const loginData = {
        email: data.email,
        password: data.password,
        _timestamp: Date.now(),
        _fingerprint: getDeviceFingerprint(),
        ...(turnstileToken && { 'cf-turnstile-response': turnstileToken })
      };
      
      const result = await login(loginData.email, loginData.password, loginData);
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError('root', { message: result.message });
      }
    } catch (error) {
      setError('root', { message: 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
            <Calculator className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Welcome back to WealthCore
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to your account to continue managing your wealth
          </p>
        </div>

        {/* Login Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Email Field */}
            <ValidatedInput
              name="email"
              label="Email address"
              type="email"
              register={register}
              errors={errors}
              placeholder="Enter your email"
              icon={Mail}
              required
              autoComplete="email"
              {...register('email', validationRules.email)}
            />

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                  className={`appearance-none relative block w-full px-3 py-3 pl-10 pr-10 border ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center z-10"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.password && (
                <div className="mt-1 flex items-center gap-1 text-sm text-red-600">
                  <span>{errors.password.message}</span>
                </div>
              )}
            </div>
          </div>

          <TurnstileWidget
            action="login"
            onVerify={(token) => setTurnstileToken(token)}
          />

          {/* Honeypot fields (hidden from users, visible to bots) */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
            <input
              type="text"
              name="website"
              tabIndex="-1"
              autoComplete="off"
              {...register('website')}
            />
            <input
              type="text"
              name="url"
              tabIndex="-1"
              autoComplete="off"
              {...register('url')}
            />
            <input
              type="text"
              name="phone_number"
              tabIndex="-1"
              autoComplete="off"
              {...register('phone_number')}
            />
            <input
              type="text"
              name="company"
              tabIndex="-1"
              autoComplete="off"
              {...register('company')}
            />
          </div>

          {/* Root Error */}
          {errors.root && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-red-800">{errors.root.message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isLoading || (isTurnstileEnabled() && !turnstileToken)}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </div>

          {/* Links */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
              >
                Sign up here
              </Link>
            </p>
          </div>
        </form>

        {/* Demo Account - Try it out */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Try it out</h3>
          <p className="text-xs text-blue-700 mb-2">
            Use our demo account to explore the platform:
          </p>
          <p className="text-sm font-mono text-blue-900 bg-blue-100 px-3 py-2 rounded">
            Email: demo@wealthcore.app<br />
            Password: Demo123!
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
